import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CATEGORY_CONFIG, type TalentCategory } from './talentMapShared';
import type { TalentSnapshot } from './VersionTimeline';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface Props {
  snapshots: TalentSnapshot[];
  finalCategory?: string | null;
  processStatus?: string | null;
  updatedAt?: string | null;
}

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Right panel of the Histórico tab: Resumen counters, Trayectoria
 *  (month → category chip, derived from snapshots) and current category. */
export function HistorySummaryPanel({ snapshots, finalCategory, processStatus, updatedAt }: Props) {
  const { t } = useTranslation();

  const summary = useMemo(() => {
    const categoryChanges = snapshots.filter((s, i) => {
      const prev = s.previousCategory !== undefined ? s.previousCategory : (snapshots[i - 1]?.category ?? null);
      return prev != null && prev !== s.category;
    }).length;
    const hrbpCount = snapshots.filter(s => s.source === 'hrbp').length;
    return {
      total: snapshots.length,
      categoryChanges,
      hrbpCount,
      first: snapshots[0]?.timestamp ?? null,
      last: snapshots[snapshots.length - 1]?.timestamp ?? null,
    };
  }, [snapshots]);

  // One point per month, keeping the last snapshot of each month, ascending
  const trajectory = useMemo(() => {
    const byMonth = new Map<string, TalentSnapshot>();
    for (const s of snapshots) {
      byMonth.set(format(new Date(s.timestamp), 'LLL yyyy', { locale: es }), s);
    }
    return Array.from(byMonth.entries());
  }, [snapshots]);

  const currentCfg = finalCategory ? CATEGORY_CONFIG[finalCategory as TalentCategory] : null;
  const rowCls = 'flex items-center justify-between text-sm';
  const fmt = (d: string | null) => (d ? format(new Date(d), 'dd/MM/yyyy', { locale: es }) : '—');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold mb-3">{t('performance.talent_map.history_tab.summary')}</h3>
        <div className="space-y-2">
          <div className={rowCls}>
            <span className="text-muted-foreground">{t('performance.talent_map.history_tab.total_versions')}</span>
            <span className="font-semibold tabular-nums">{summary.total}</span>
          </div>
          <div className={rowCls}>
            <span className="text-muted-foreground">{t('performance.talent_map.history_tab.category_changes')}</span>
            <span className="font-semibold tabular-nums">{summary.categoryChanges}</span>
          </div>
          <div className={rowCls}>
            <span className="text-muted-foreground">{t('performance.talent_map.history_tab.hrbp_modifications')}</span>
            <span className="font-semibold tabular-nums">{summary.hrbpCount}</span>
          </div>
          <div className={rowCls}>
            <span className="text-muted-foreground">{t('performance.talent_map.history_tab.first_record')}</span>
            <span className="font-semibold tabular-nums">{fmt(summary.first)}</span>
          </div>
          <div className={rowCls}>
            <span className="text-muted-foreground">{t('performance.talent_map.history_tab.last_update')}</span>
            <span className="font-semibold tabular-nums">{fmt(summary.last)}</span>
          </div>
        </div>
      </div>

      {trajectory.length > 0 && (
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3">{t('performance.talent_map.history_tab.trajectory')}</h3>
          <div className="space-y-0.5">
            {trajectory.map(([month, snap], i) => {
              const cfg = CATEGORY_CONFIG[snap.category as TalentCategory];
              return (
                <div key={month}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-muted-foreground">{capitalize(month)}</span>
                    {cfg && (
                      <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap', cfg.chip)}>
                        {t(cfg.labelKey)}
                      </span>
                    )}
                  </div>
                  {i < trajectory.length - 1 && <span className="block w-px h-3 bg-border ml-2 my-0.5" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {currentCfg && (
        <div className="border-t pt-4">
          <h3 className="font-semibold mb-3">{t('performance.talent_map.history_tab.current_category')}</h3>
          <div className={cn('rounded-lg py-2.5 text-center text-sm font-semibold', currentCfg.chip)}>
            {t(currentCfg.labelKey)}
          </div>
          <p className="text-xs text-muted-foreground text-center mt-2">
            {processStatus === 'hrbp_modified'
              ? t('performance.talent_map.history_tab.updated_by_hrbp')
              : t('performance.talent_map.history_tab.confirmed_by_leader')}
            {updatedAt && <> · {fmt(updatedAt)}</>}
          </p>
        </div>
      )}
    </div>
  );
}
